import { useHub } from './context';
import type { Hub } from './hub';
import type { TriumphDefs } from './triumphs';
import { Card, ObjectiveStrip, Pic } from './ui';

/** Shared triumph pieces (the app's RecordRow and Count). */

const counts = new WeakMap<Hub, Map<string, { done: number; total: number }>>();

/** Completed and total triumphs under a category (its own and every sub-category's), hidden ones and gildings left out. */
export function countUnder(defs: TriumphDefs, hub: Hub, charId: string | undefined, node: number): { done: number; total: number } {
  let byHub = counts.get(hub);
  if (!byHub) { byHub = new Map(); counts.set(hub, byHub); }
  const key = `${node}|${charId ?? ''}`;
  const known = byHub.get(key);
  if (known) return known;
  const all = new Set<number>();
  const walk = (n: number) => { const d = defs.nodes.get(n); if (!d) return; d.records.forEach((r) => all.add(r)); d.nodes.forEach(walk); };
  walk(node);
  let done = 0, total = 0;
  for (const r of all) {
    const l = hub.record(r, charId);
    if (!l || l.hidden || !defs.records.get(r) || defs.records.get(r)!.gilding) continue;
    total++;
    if (l.done) done++;
  }
  const c = { done, total };
  byHub.set(key, c);
  return c;
}

/** The live triumph for the character shown, as a stable function (so counts can be remembered per character). */
export function useLive() {
  const { hub, char } = useHub();
  const id = char?.id;
  return (hash: number) => hub?.record(hash, id);
}

const PINS = 'd2c.pins.triumphs';
export const readPins = (): number[] => { try { return JSON.parse(localStorage.getItem(PINS) ?? '[]'); } catch { return []; } };
export const isPinned = (h: number) => readPins().includes(h);
export function togglePin(h: number) {
  const p = readPins();
  try { localStorage.setItem(PINS, JSON.stringify(p.includes(h) ? p.filter((x) => x !== h) : [h, ...p])); } catch { /* storage blocked */ }
}

/** A triumph as a card: icon, name, score, description, its objectives, and a pill saying where it stands. */
export function RecordRow({ hash, onPin }: { hash: number; onPin?: () => void }) {
  const { defs } = useHub();
  const live = useLive()(hash);
  const def = defs?.records.get(hash);
  const secret = !!live?.secret && !live.done;
  const done = !!live?.done;
  let lines = live ? (live.steps ? [live.objectives.find((o) => !o.done) ?? live.objectives[live.objectives.length - 1]] : [...live.objectives].sort((a, b) => Number(a.done) - Number(b.done))) : [];
  lines = lines.filter(Boolean);
  const showLines = !secret && live && !(done && live.objectives.length <= 1);
  const [label, tone] = !live ? ['UNKNOWN', 'var(--text3)'] : live.done ? ['COMPLETED', 'var(--good)'] : live.secret ? ['SECRET', 'var(--text3)'] : live.locked ? ['LOCKED', 'var(--warn)'] : [`${Math.round(live.fraction * 100)} %`, 'var(--info)'];
  const pinned = isPinned(hash);
  return (
    <Card className="g-record" done={done}>
      <Pic src={secret ? undefined : def?.icon} className="g-record-icon" style={{ opacity: done ? 1 : 0.8 }} />
      <div className="g-record-main">
        <div className="g-record-title">
          <b className={done ? 'good' : ''}>{secret ? 'Secret triumph' : def?.name ?? 'Unknown triumph'}</b>
          {def && def.score > 0 && <span className="gold">+{def.score}</span>}
          {def?.gilding && <span className="g-tag accent">GILDING</span>}
        </div>
        <p className="g-text2">{secret ? 'Keep playing to uncover this triumph.' : def?.desc ?? ''}</p>
        {showLines && lines.slice(0, 6).map((o, i) => (
          <ObjectiveStrip key={i} text={defs?.objectives.get(o.hash)?.text || 'Progress'} progress={o.progress} goal={o.goal} done={o.done} />
        ))}
        {showLines && lines.length > 6 && <p className="g-dim small">and {lines.length - 6} more ({lines.slice(6).filter((o) => o.done).length} done)</p>}
        {showLines && live!.steps && live!.objectives.length > 1 && <p className="g-dim small">Stage {Math.min(live!.objectives.length, live!.objectives.filter((o) => o.done).length + 1)} of {live!.objectives.length}</p>}
      </div>
      <div className="g-record-side">
        <span className="g-pill" style={{ background: tone }} title={live?.locked ? 'Needs content this account does not own.' : undefined}>{label}</span>
        {!secret && (
          <button type="button" className={`g-track ${pinned ? 'on' : ''}`} title={pinned ? 'Stop tracking this triumph' : 'Track this triumph: it is then listed first on the Triumphs front page'}
            onClick={() => { togglePin(hash); onPin?.(); }}>{pinned ? 'TRACKED' : 'TRACK'}</button>
        )}
      </div>
    </Card>
  );
}
